<script lang="ts">
  import { onDestroy } from 'svelte';
  import { api, navigate, type Me } from '../lib/api';
  import { AudioOut } from '../lib/audio';
  import { keepAwake, openMic, toggleFullscreen } from '../lib/device';
  import { LatencyProbe } from '../lib/latency';
  import { Session } from '../lib/session.svelte';
  import { describeTarget, can } from '../../../shared/src/permissions';
  import type { PositionConfig } from '../../../shared/src/types';
  import ConnBadge from '../components/ConnBadge.svelte';
  import PttButton from '../components/PttButton.svelte';
  import StartGate from '../components/StartGate.svelte';
  import VideoView from '../components/VideoView.svelte';

  let { principal, number }: { principal: NonNullable<Me['principal']>; number: number } = $props();

  let position = $state<PositionConfig | null>(null);
  let lookupError = $state<string | null>(null);
  let session = $state<Session | null>(null);
  let micError = $state<string | null>(null);
  let video = $state<HTMLVideoElement | null>(null);
  let probe: LatencyProbe | null = null;

  // UI state (persisted per device)
  const saved = JSON.parse(localStorage.getItem('vtb.operator') ?? '{}');
  let returnVol = $state<number>(saved.returnVol ?? 0.8);
  let talkVol = $state<number>(saved.talkVol ?? 1);
  let tallyFull = $state<boolean>(saved.tallyFull ?? false);
  let showStats = $state<boolean>(saved.showStats ?? false);
  let lowBw = $state<boolean>(saved.lowBw ?? false);
  let latched = $state(false);
  let menuOpen = $state(false);
  let controlsVisible = $state(true);
  let hideTimer: number | undefined;

  $effect(() => {
    localStorage.setItem('vtb.operator', JSON.stringify({ returnVol, talkVol, tallyFull, showStats, lowBw }));
    session?.audio.setVolume('return', returnVol);
    session?.audio.setVolume('talk', talkVol);
  });

  async function lookup() {
    try {
      const list = await api.get<Array<PositionConfig>>('/api/positions');
      // Link-token sessions are bound to one position server-side: follow it, whatever the URL says.
      const tokenPos = principal.kind === 'token' ? list.find((p) => p.id === principal.positionId) : undefined;
      if (tokenPos && tokenPos.number !== number) {
        navigate(`/cam/${tokenPos.number}`, true);
        return;
      }
      position = tokenPos ?? list.find((p) => p.number === number) ?? null;
      if (!position) lookupError = `Camera ${number} does not exist`;
    } catch (err) {
      lookupError = (err as Error).message;
    }
  }
  void lookup();

  const canTalkRole = $derived(can(principal.role, 'talk'));

  async function start() {
    if (!position) return;
    let mic: MediaStream | null = null;
    // Unlock playback synchronously inside the tap, before any await (iOS gesture rules).
    const audio = new AudioOut();
    audio.unlock();
    audio.setVolume('return', returnVol);
    audio.setVolume('talk', talkVol);
    if (canTalkRole) {
      try {
        mic = await openMic();
      } catch (err) {
        micError = (err as Error).message;
      }
    }
    void keepAwake(true);
    session = new Session({ positionId: position.id, mic, audio });
    session.lowBandwidth = lowBw;
    (window as unknown as { __vtb: Session }).__vtb = session; // debugging / automated tests
    void session.start();
    poke();
  }

  onDestroy(() => {
    probe?.stop();
    session?.stop();
    void keepAwake(false);
  });

  // Latency probe follows the video element / stream.
  $effect(() => {
    if (!video || !session) return;
    const s = session;
    probe?.stop();
    probe = new LatencyProbe(video, (t) => s.clock.serverNow(t), (ms) => s.setMeasuredLatency(ms));
    probe.start();
    return () => probe?.stop();
  });

  function poke() {
    controlsVisible = true;
    clearTimeout(hideTimer);
    hideTimer = window.setTimeout(() => {
      if (!menuOpen) controlsVisible = false;
    }, 5000);
  }

  const tally = $derived(position && session ? session.tally[position.id] : undefined);
  const tallyState = $derived(tally?.state ?? 'off');
  const health = $derived(session?.sourceId ? session.sources[session.sourceId] : undefined);
  // Camera-enabled positions get the call grid: return feed + every other browser camera + self-view.
  const callView = $derived(!!position?.allowVideo);
  const peers = $derived(Object.entries(session?.peerStreams ?? {}));
  const gridCols = $derived(Math.ceil(Math.sqrt(1 + peers.length + (session?.localVideo ? 1 : 0))));
  const peerName = (id: string) => session?.browserSources.find((b) => b.id === id)?.name ?? 'Camera';
  async function toggleCamera() {
    if (!session) return;
    if (session.cameraOn) await session.stopCamera();
    else await session.startCamera().catch(() => undefined);
  }
  const noSignal = $derived(!!session && (!session.sourceId || !health || !health.signal || !session.videoStream));
  const others = $derived((session?.talkers ?? []).filter((t) => t.id !== session?.me?.id));
  const callsForMe = $derived(
    (session?.calls ?? []).filter(
      (c) => c.fromId !== session?.me?.id && (c.to.kind === 'all' || (c.to.kind === 'position' && c.to.positionId === position?.id)),
    ),
  );
  const myCall = $derived((session?.calls ?? []).find((c) => c.fromId === session?.me?.id));
  const posName = (id: string) => session?.positions.find((p) => p.id === id)?.name ?? 'position';

  function talk(active: boolean) {
    void session?.setTalking(active, { kind: 'partyline' });
  }

  async function toggleCall() {
    if (!session) return;
    if (myCall) await session.callAck(myCall.fromId);
    else await session.call(true, { kind: 'directors' });
  }

  async function setLowBw(v: boolean) {
    lowBw = v;
    await session?.setLowBandwidth(v);
  }

  async function leave() {
    session?.stop();
    session = null;
    if (principal.kind === 'user') navigate('/join');
    else {
      await api.post('/api/logout');
      location.href = '/';
    }
  }
</script>

<svelte:window onpointerdown={poke} onpointermove={poke} onkeydown={poke} />

{#if lookupError}
  <div class="page"><div class="panel"><p class="error">{lookupError}</p><button onclick={() => navigate('/join')}>Back</button></div></div>
{:else if !position}
  <div class="page muted">Loading…</div>
{:else if !session}
  <StartGate title={position.name} subtitle={`Camera ${position.number}`} onstart={start} />
{:else}
  <div class="op tally-{tallyState}" class:full={tallyFull}>
    {#if tallyFull}
      <div class="fullTally">
        <div class="bigNum">{position.number}</div>
        <div class="bigState">
          {tallyState === 'program' ? 'ON AIR' : tallyState === 'preview' ? 'PREVIEW' : tallyState === 'unknown' ? 'TALLY DISCONNECTED' : ''}
        </div>
        <div class="bigName">{position.name}</div>
      </div>
    {:else}
      <div class="videoBox" class:callGrid={callView} style:--cols={gridCols}>
        <div class="tile">
          <VideoView stream={session.videoStream} bind:video />
          {#if noSignal}
            <div class="slate">
              <div class="ns">{session.sourceId ? 'NO SIGNAL' : 'NO RETURN FEED'}</div>
              <div class="muted">{session.sourceName ?? ''}{health?.state && health.state !== 'running' ? ` · ${health.state}` : ''}</div>
            </div>
          {/if}
          {#if callView}<span class="tileName">{session.sourceName ?? 'Return feed'}</span>{/if}
        </div>
        {#if callView}
          {#each peers as [id, stream] (id)}
            <div class="tile">
              <VideoView {stream} />
              <span class="tileName">{peerName(id)}</span>
            </div>
          {/each}
          {#if session.localVideo}
            <div class="tile self" class:mirror={session.facing === 'user'}>
              <VideoView stream={session.localVideo} fit="cover" />
              <span class="tileName">You</span>
            </div>
          {/if}
        {/if}
      </div>
    {/if}

    <!-- Always-visible top bar: identity, tally, connection -->
    <div class="top">
      <div class="name">{position.name}</div>
      {#if !tallyFull && tallyState === 'program'}<div class="tallyLabel pgm">ON AIR</div>{/if}
      {#if !tallyFull && tallyState === 'preview'}<div class="tallyLabel pvw">PREVIEW</div>{/if}
      <div class="grow"></div>
      <ConnBadge {session} />
    </div>

    {#if tallyState === 'unknown'}
      <div class="banner warn">TALLY DISCONNECTED</div>
    {:else if tally?.degraded}
      <div class="banner warn small">Tally partially disconnected</div>
    {/if}
    {#if session.status === 'kicked'}
      <div class="banner bad">{session.error}<button class="small" onclick={() => location.reload()}>Reconnect</button></div>
    {:else if session.status === 'reconnecting'}
      <div class="banner bad">Connection lost — reconnecting…{session.error ? ` (${session.error})` : ''}</div>
    {/if}
    {#if micError}<div class="banner warn small">{micError} — listen only</div>{/if}
    {#if session.cameraError}<div class="banner warn small">Camera: {session.cameraError}</div>{/if}

    {#each callsForMe as c (c.fromId)}
      <button class="banner call flash" onclick={() => session?.callAck(c.fromId)}>📣 {c.fromName} is calling you — tap to answer</button>
    {/each}

    {#if others.length}
      <div class="talkers">
        {#each others as t (t.id)}
          <span class="talker">🎙 {t.name}{t.target && t.target.kind !== 'partyline' ? ` → ${describeTarget(t.target, posName)}` : ''}</span>
        {/each}
      </div>
    {/if}

    {#if showStats}
      <div class="stats mono">
        <div>latency {session.stats.measuredLatencyMs ?? '–'} ms</div>
        <div>playout {session.stats.playoutDelayMs ?? '–'} ms</div>
        <div>rtt {session.stats.rttMs !== null ? Math.round(session.stats.rttMs) : '–'} ms · loss {session.stats.lossPct ?? '–'}%</div>
        <div>{session.stats.videoKbps ?? '–'} kbps · {session.stats.fps ?? '–'} fps{lowBw ? ' · LOW' : ''}</div>
        {#if health}<div>src {health.width}×{health.height} {health.fps}{health.interlaced ? 'i' : 'p'} · {health.encoder}</div>{/if}
      </div>
    {/if}

    <!-- Bottom controls; PTT always reachable with a thumb -->
    <div class="bottom" class:hidden={!controlsVisible && !menuOpen}>
      {#if menuOpen}
        <div class="menu panel">
          <label>Return audio <input type="range" min="0" max="1" step="0.01" bind:value={returnVol} /></label>
          <label>Talkback <input type="range" min="0" max="1" step="0.01" bind:value={talkVol} /></label>
          <label class="inline"><input type="checkbox" checked={lowBw} onchange={(e) => setLowBw(e.currentTarget.checked)} /> Low bandwidth (Wi-Fi)</label>
          <label class="inline"><input type="checkbox" bind:checked={tallyFull} /> Full-screen tally</label>
          <label class="inline"><input type="checkbox" bind:checked={showStats} /> Show stats</label>
          {#if session.canTalk}<label class="inline"><input type="checkbox" bind:checked={latched} onchange={() => talk(latched)} /> Latch talk</label>{/if}
          <div class="row">
            <button onclick={toggleFullscreen}>Fullscreen</button>
            <button onclick={leave}>Leave</button>
          </div>
        </div>
      {/if}
      <div class="row ctrl">
        <button class="round" onclick={() => (menuOpen = !menuOpen)} aria-label="Settings">☰</button>
        {#if can(principal.role, 'call')}
          <button class="round callBtn" class:active={!!myCall} class:flash={!!myCall} onclick={toggleCall}>{myCall ? 'CALLING' : 'CALL'}</button>
        {/if}
        {#if session.videoSourceId}
          <button class="round camBtn" class:active={session.cameraOn} onclick={toggleCamera} aria-label="Camera">{session.cameraOn ? '📷 ON' : '📷'}</button>
          {#if session.cameraOn}<button class="round" onclick={() => session?.flipCamera()} aria-label="Switch camera">🔄</button>{/if}
        {/if}
        <div class="grow"></div>
      </div>
    </div>
    {#if session.canTalk && session.micLive}
      <div class="pttWrap">
        <PttButton onchange={talk} active={session.talking} bind:latched />
      </div>
    {/if}
  </div>
{/if}

<style>
  .op { position: fixed; inset: 0; background: #000; overflow: hidden; --tb: 0px; --tc: transparent; }
  /* Thick tally border drawn inside the viewport so it is visible on any screen shape. */
  .op::after { content: ''; position: absolute; inset: 0; border: var(--tb) solid var(--tc); pointer-events: none; z-index: 5; }
  .tally-program { --tb: 14px; --tc: var(--program); }
  .tally-preview { --tb: 14px; --tc: var(--preview); }
  .tally-unknown { --tb: 6px; --tc: #555; }
  .videoBox { position: absolute; inset: 0; }
  .videoBox .tile { position: absolute; inset: 0; }
  .callGrid {
    display: grid; grid-template-columns: repeat(var(--cols), 1fr); grid-auto-rows: 1fr; gap: 6px;
    padding: calc(max(18px, env(safe-area-inset-top)) + 56px) 20px 100px; background: #000;
  }
  .callGrid .tile { position: relative; inset: auto; background: #111; border-radius: 10px; overflow: hidden; min-height: 0; }
  .tileName { position: absolute; left: 8px; bottom: 8px; background: rgba(0,0,0,.7); padding: 2px 8px; border-radius: 6px; font-weight: 700; font-size: 0.85rem; }
  .self { outline: 2px solid var(--accent); }
  .mirror :global(video) { transform: scaleX(-1); }
  .camBtn { font-size: 1rem; }
  .camBtn.active { background: var(--program); }
  .slate { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #111; gap: 8px; }
  .ns { font-size: clamp(2rem, 9vw, 5rem); font-weight: 900; color: var(--program); letter-spacing: 0.05em; }
  .top {
    position: absolute; top: 0; left: 0; right: 0; z-index: 6; display: flex; align-items: center; gap: 10px;
    padding: max(18px, env(safe-area-inset-top)) 22px 10px; background: linear-gradient(rgba(0,0,0,.75), transparent);
  }
  .name { font-size: clamp(1.2rem, 4.5vw, 2rem); font-weight: 900; text-shadow: 0 2px 6px #000; }
  .tallyLabel { font-weight: 900; padding: 4px 12px; border-radius: 8px; font-size: clamp(1rem, 4vw, 1.6rem); }
  .pgm { background: var(--program); color: #fff; }
  .pvw { background: var(--preview); color: #000; }
  .banner {
    position: relative; z-index: 6; margin: 4px 22px; padding: 10px 14px; border-radius: 10px; font-weight: 800; display: flex; gap: 12px; align-items: center;
    top: calc(max(18px, env(safe-area-inset-top)) + 52px);
  }
  .banner.warn { background: var(--warn); color: #000; }
  .banner.bad { background: #8a1010; color: #fff; }
  .banner.small { font-size: 0.85rem; font-weight: 600; }
  .banner.call { width: calc(100% - 44px); background: var(--accent); color: #fff; border: 0; font-size: 1.2rem; justify-content: center; }
  .talkers { position: absolute; z-index: 6; left: 22px; bottom: 120px; display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
  .talker { background: rgba(255, 204, 0, 0.92); color: #000; font-weight: 800; padding: 6px 12px; border-radius: 999px; }
  .stats { position: absolute; z-index: 6; right: 22px; top: 80px; background: rgba(0,0,0,.7); padding: 8px 12px; border-radius: 8px; font-size: 0.8rem; line-height: 1.45; }
  .bottom { position: absolute; z-index: 7; left: 0; right: 0; bottom: 0; padding: 12px 22px max(22px, env(safe-area-inset-bottom)); transition: opacity 0.3s; }
  .bottom.hidden { opacity: 0; pointer-events: none; }
  .menu { display: flex; flex-direction: column; gap: 12px; max-width: 360px; margin-bottom: 12px; background: rgba(21,23,26,.95); }
  .round { width: 64px; height: 64px; border-radius: 50%; padding: 0; font-size: 1.4rem; background: rgba(31,34,38,.85); }
  .callBtn { font-size: 0.85rem; font-weight: 800; }
  .callBtn.active { background: var(--accent); }
  .pttWrap { position: absolute; z-index: 8; right: max(22px, env(safe-area-inset-right)); bottom: max(22px, env(safe-area-inset-bottom)); }
  .fullTally { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #000; }
  .full.tally-program .fullTally { background: var(--program); }
  .full.tally-preview .fullTally { background: var(--preview); color: #000; }
  .full.tally-unknown .fullTally { background: #333; }
  .bigNum { font-size: 40vmin; font-weight: 900; line-height: 1; }
  .bigState { font-size: 9vmin; font-weight: 900; }
  .bigName { font-size: 5vmin; font-weight: 700; opacity: 0.8; }
</style>
